import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { supabase } from "@/integrations/supabase/client";
import ProjectCard from "./ProjectCard";
import BeforeAfterSlider from "./BeforeAfterSlider";
import YouTubePlayer from "./YouTubePlayer";
interface Project {
  id: string;
  title: string;
  description: string;
  image: string;
  tags: string[];
  demoUrl?: string;
  githubUrl?: string;
  videoUrl?: string;
  isSchoolProject: boolean;
  longDescription?: string;
  color: 'yellow' | 'blue' | 'green' | 'pink';
  previewImages?: string[];
  displayType?: 'carousel' | 'before_after' | 'video';
  beforeAfter?: {
    before: string;
    after: string;
  };
}
const ProjectsSection = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [previewProject, setPreviewProject] = useState<Project | null>(null);
  const [filter, setFilter] = useState<"all" | "school" | "personal">("all");
  const [koachDisclaimerAccepted, setKoachDisclaimerAccepted] = useState<boolean>(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("is_visible", true)
        .order("display_order", { ascending: true });

      if (error) throw error;

        // Transform database data to match component interface
        const transformedProjects: Project[] = (data || []).map(project => ({
          id: project.id,
          title: project.title,
          description: project.description,
          longDescription: project.long_description,
          image: project.image || "/placeholder.svg",
          tags: project.tags || [],
          demoUrl: project.demo_url,
          githubUrl: project.github_url,
          videoUrl: project.video_url,
          isSchoolProject: project.is_school_project,
          color: project.color as 'yellow' | 'blue' | 'green' | 'pink',
          displayType: (project.display_type || 'carousel') as 'carousel' | 'before_after' | 'video',
          previewImages: project.preview_images || [],
          beforeAfter: project.before_after_before && project.before_after_after ? {
            before: project.before_after_before,
            after: project.before_after_after
          } : undefined
        }));

      setProjects(transformedProjects);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const filteredProjects = projects.filter(project => {
    if (filter === "school") return project.isSchoolProject;
    if (filter === "personal") return !project.isSchoolProject;
    return true;
  });
  const filters = [{
    key: "all",
    label: "Tous les projets",
    count: projects.length
  }, {
    key: "school",
    label: "Projets d'école",
    count: projects.filter(p => p.isSchoolProject).length
  }, {
    key: "personal",
    label: "Projets professionnels",
    count: projects.filter(p => !p.isSchoolProject).length
  }];
  if (isLoading) {
    return (
      <section id="projects" className="py-20 bg-foreground text-background">
        <div className="container mx-auto px-8">
          <div className="text-center">
            <p className="text-xl">Chargement des projets...</p>
          </div>
        </div>
      </section>
    );
  }

  return <section id="projects" className="py-20 bg-foreground text-background">
      <div className="container mx-auto px-8">
        <div className="max-w-4xl mb-20">
          <h2 className="text-huge font-black mb-8 leading-none">
            Mes projets
            <br />
            <span className="bg-electric text-electric-foreground px-4 py-1 mt-2 inline-block">& réalisations</span>
          </h2>
          <p className="text-xl md:text-2xl text-background/70 leading-relaxed">
            Découvrez une sélection de projets UX/UI, développement web React, 
            applications mobiles et stratégies digitales qui reflètent mon expertise technique.
          </p>
        </div>

        {/* Filtres style Nobilito */}
        <div className="flex flex-wrap gap-4 mb-16">
          {filters.map(({
          key,
          label,
          count
        }) => <button key={key} onClick={() => setFilter(key as typeof filter)} className={`px-6 py-3 rounded-nobilito font-semibold text-lg transition-all hover-lift ${filter === key ? "bg-background text-foreground" : "bg-background/10 text-background hover:bg-background/20"}`}>
              {label} ({count})
            </button>)}
        </div>

        {/* Grille de projets alignée horizontalement */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => <div key={project.id} className="animate-fade-in" style={{
          animationDelay: `${index * 0.1}s`
        }}>
              <ProjectCard project={project} onViewDetails={setSelectedProject} onPreview={setPreviewProject} />
            </div>)}
        </div>

        {/* Modal de détails */}
        <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
          <DialogContent className="max-w-2xl rounded-nobilito">
            <DialogHeader>
              <DialogTitle className="text-3xl font-black">
                {selectedProject?.title}
              </DialogTitle>
            </DialogHeader>
            
            {selectedProject && <div className="space-y-6">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {selectedProject.longDescription || selectedProject.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {selectedProject.tags.map(tag => <span key={tag} className="px-4 py-2 bg-muted rounded-nobilito text-sm font-medium">
                      {tag}
                    </span>)}
                </div>
                
                <div className="flex gap-4 pt-4">
                  {selectedProject.demoUrl && <Button asChild className="rounded-nobilito font-semibold">
                      <a href={selectedProject.demoUrl} target="_blank" rel="noopener noreferrer">
                        Voir le projet
                      </a>
                    </Button>}
                  {selectedProject.githubUrl && <Button variant="outline" asChild className="rounded-nobilito font-semibold">
                      <a href={selectedProject.githubUrl} target="_blank" rel="noopener noreferrer">
                        Code source
                      </a>
                    </Button>}
                </div>
              </div>}
          </DialogContent>
        </Dialog>

        {/* Modal d'aperçu avec carrousel */}
        <Dialog open={!!previewProject} onOpenChange={() => {
        setPreviewProject(null);
        setKoachDisclaimerAccepted(false);
        setFullscreenImage(null);
      }}>
          <DialogContent className="max-w-7xl max-h-[90vh] rounded-nobilito overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">
                Aperçu - {previewProject?.title}
              </DialogTitle>
            </DialogHeader>
            
            {previewProject && <div className="space-y-4">                
                {/* Disclaimer pour Koach Backoffice */}
                {previewProject.id === "6" && !koachDisclaimerAccepted ? <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-yellow-800">Information Importante</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Les images exposées reflètent les fonctions principales du back-office mais ne contiennent évidemment pas d'informations réelles, 
                            étant donné que celles-ci doivent rester <strong>confidentielles</strong>. En revanche, elles ont été remplacées par des exemples 
                            afin que celui qui regarde puisse s'en approcher.
                          </p>
                        </div>
                        <div className="mt-4">
                          <Button onClick={() => setKoachDisclaimerAccepted(true)} className="bg-yellow-600 text-white hover:bg-yellow-700">
                            J'en prends conscience
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div> : <>
                    {/* Système avant/après */}
                    {previewProject.displayType === 'before_after' && previewProject.beforeAfter ? <div className="space-y-4">
                        <BeforeAfterSlider beforeImage={previewProject.beforeAfter.before} afterImage={previewProject.beforeAfter.after} beforeLabel="Avant" afterLabel="Après" />
                        <p className="text-center text-sm text-muted-foreground">
                          Glissez la barre pour comparer les versions
                        </p>
                      </div> : 
                    /* Carrousel normal pour les autres projets */
                    previewProject.displayType === 'carousel' && previewProject.previewImages && previewProject.previewImages.length > 0 ? (
                      <div className="space-y-4">
                        <Carousel className="w-full">
                          <CarouselContent>
                            {previewProject.previewImages.map((image, index) => <CarouselItem key={index}>
                                <div className="flex justify-center items-center bg-muted rounded-nobilito overflow-hidden min-h-[500px]">
                                  <img src={image} alt={`Aperçu ${index + 1} de ${previewProject.title}`} className="max-h-[700px] max-w-full object-contain cursor-zoom-in hover:opacity-80 transition-opacity" onClick={() => setFullscreenImage(image)} />
                                </div>
                              </CarouselItem>)}
                          </CarouselContent>
                          {previewProject.previewImages.length > 1 && <>
                              <CarouselPrevious className="left-4" />
                              <CarouselNext className="right-4" />
                            </>}
                        </Carousel>
                        <p className="text-center text-sm text-muted-foreground">
                          {previewProject.previewImages?.length || 0} image{(previewProject.previewImages?.length || 0) > 1 ? 's' : ''} • Utilisez les flèches pour naviguer
                        </p>
                      </div>
                    ) : 
                    /* Lecteur vidéo YouTube */
                    previewProject.displayType === 'video' && previewProject.videoUrl ? (
                      <div className="space-y-4">
                        <YouTubePlayer 
                          url={previewProject.videoUrl}
                          title={previewProject.title}
                          className="w-full"
                        />
                        <p className="text-center text-sm text-muted-foreground">
                          Cliquez sur le bouton de lecture pour voir la vidéo
                        </p>
                      </div>
                    ) : null}
                  </>}
              </div>}
          </DialogContent>
        </Dialog>

        {/* Modal plein écran pour les images */}
        <Dialog open={!!fullscreenImage} onOpenChange={() => setFullscreenImage(null)}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-2 bg-black/90 border-none">
            <div className="flex items-center justify-center h-full">
              <img src={fullscreenImage || ""} alt="Image en plein écran" className="max-w-full max-h-full object-contain" />
            </div>
            <button onClick={() => setFullscreenImage(null)} className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors">
              ✕
            </button>
          </DialogContent>
        </Dialog>
      </div>
    </section>;
};
export default ProjectsSection;